import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';

const DATE_PRESETS = [
  { label: '24h', value: '1' },
  { label: '48h', value: '2' },
  { label: '3 days', value: '3' },
  { label: '7 days', value: '7' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
  { label: 'Custom', value: 'custom' },
];

function Funnel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [expandedStep, setExpandedStep] = useState(null);
  const [campaignFilter, setCampaignFilter] = useState('');
  const [selectedTile, setSelectedTile] = useState(null);

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (selectedPreset === 'custom' && customStart && customEnd) {
        params.startDate = customStart;
        params.endDate = customEnd;
      } else {
        params.days = selectedPreset;
      }

      const response = await api.get('/marketing/funnel', { params });
      setData(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedPreset, customStart, customEnd]);

  useEffect(() => {
    if (selectedPreset !== 'custom' || (customStart && customEnd)) {
      fetchFunnel();
    }
  }, [fetchFunnel]);

  const handlePresetChange = (val) => {
    setSelectedPreset(val);
    setExpandedStep(null);
  };

  if (loading) {
    return (
      <div className="mkt-loading">
        <div className="mkt-loading-spinner" />
        <p>Loading funnel data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mkt-funnel-error">
        <p>Failed to load funnel: {error}</p>
        <button onClick={fetchFunnel} className="mkt-btn mkt-btn-primary">Retry</button>
      </div>
    );
  }

  const { funnel = [], summary = {} } = data || {};

  // Campaign filter options come from the loaded visitors (ad-resolved
  // campaign preferred, UTM campaign as fallback)
  const campaigns = [...new Set(
    funnel.flatMap((s) => (s.visitors || []).map((v) => v.adCampaign || v.utmCampaign).filter(Boolean))
  )].sort();
  const matchesCampaign = (v) => !campaignFilter || (v.adCampaign || v.utmCampaign) === campaignFilter;

  // Flattened people with their furthest-step index, for tile drill-downs
  const allPeople = funnel.flatMap((s, i) =>
    (s.visitors || []).map((v) => ({ ...v, stepIndex: i, stepId: s.id, stepName: s.name }))
  );
  // Tiles are keyed by step id, resolved against the funnel the API returned —
  // no positional index literals (inserting a funnel step must never shift these).
  const stepIndexById = Object.fromEntries(funnel.map((s, i) => [s.id, i]));
  const stepById = Object.fromEntries(funnel.map((s) => [s.id, s]));
  const TILES = [
    { key: 'landed', label: 'Landed', stepId: 'landing' },
    { key: 'started', label: 'Started', stepId: 'start' },
    { key: 'business', label: 'Business', stepId: 'business' },
    { key: 'analysis', label: 'Analysis', stepId: 'analysis' },
    { key: 'quote', label: 'Quote', stepId: 'quote' },
    { key: 'paid', label: 'Paid', stepId: 'paid' },
    { key: 'returning', label: 'Returning', stepId: null },
  ];
  const tileCohort = (tile) => {
    if (!tile) return [];
    if (tile.key === 'returning') return allPeople.filter((p) => p.isReturning);
    return allPeople.filter((p) => p.stepIndex >= (stepIndexById[tile.stepId] ?? Infinity));
  };
  const tileCount = (tile) =>
    tile.key === 'returning' ? (summary.returningVisitors || 0) : (stepById[tile.stepId]?.reached ?? 0);
  const tilePct = (tile) =>
    tile.key === 'returning' || tile.key === 'landed' ? null : (summary.pctOfLanded?.[tile.key] ?? null);
  const activeTile = TILES.find((t) => t.key === selectedTile) || null;

  // Keyword × stage matrix: how many visitors on each search keyword reached
  // each funnel stage (cumulative, same semantics as the summary tiles).
  // Campaign filter applies; visitors with no keyword aren't shown.
  const keywordSteps = TILES.filter((t) => t.stepId !== null);
  const paidCol = keywordSteps.findIndex((t) => t.key === 'paid');
  const keywordRows = (() => {
    const byKw = new Map();
    for (const p of allPeople) {
      if (!matchesCampaign(p)) continue;
      const kw = p.adKeyword || p.utmTerm;
      if (!kw) continue;
      let row = byKw.get(kw);
      if (!row) {
        row = { keyword: kw, adGroup: p.adGroup || null, counts: Array(keywordSteps.length).fill(0) };
        byKw.set(kw, row);
      }
      // Count this person into every tile column at or below their furthest step
      keywordSteps.forEach((t, i) => {
        if ((stepIndexById[t.stepId] ?? Infinity) <= p.stepIndex) row.counts[i]++;
      });
    }
    return [...byKw.values()].sort((a, b) => b.counts[0] - a.counts[0] || b.counts[paidCol] - a.counts[paidCol]);
  })();

  // Registration likelihood badge (values like HIGH / MODERATE / LOW / VERY_LOW)
  const likelihoodStyle = (val) => {
    const v = String(val).toLowerCase();
    if (v.includes('very_low') || v.includes('very low') || v.includes('low')) return { background: '#fef2f2', color: '#dc2626' };
    if (v.includes('moderate') || v.includes('medium')) return { background: '#fffbeb', color: '#b45309' };
    if (v.includes('high') || v.includes('likely')) return { background: '#ecfdf5', color: '#047857' };
    return { background: '#f1f5f9', color: '#64748b' };
  };
  const websiteChip = (v, reachedStart) => {
    if (v.websiteUrl) {
      const domain = v.websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      return (
        <a
          href={v.websiteUrl.startsWith('http') ? v.websiteUrl : `https://${v.websiteUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mkt-visitor-mode"
          style={{ background: '#f0fdfa', color: '#0f766e', textDecoration: 'none' }}
          title="Open their website"
        >
          🌐 {domain.length > 30 ? domain.slice(0, 30) + '…' : domain}
        </a>
      );
    }
    // Only meaningful once they passed the "do you have a website?" step
    if (reachedStart) {
      return <span className="mkt-visitor-mode" style={{ color: '#94a3b8' }}>no website</span>;
    }
    return null;
  };

  const contactLabel = (p) => {
    if (p.contactStatus === 'do_not_contact') return '⛔ DNC';
    if (p.outreachCount > 0) {
      return `✉️ Contacted ${p.outreachCount}×${p.lastContactedAt ? ` · ${timeAgoShort(p.lastContactedAt)}` : ''}`;
    }
    return 'Not contacted';
  };

  return (
    <div className="mkt-funnel">
      {/* Header */}
      <div className="mkt-funnel-header">
        <h2>Wizard Funnel</h2>
        <div className="mkt-funnel-filters">
          {DATE_PRESETS.map(p => (
            <button
              key={p.value}
              className={`mkt-filter-btn ${selectedPreset === p.value ? 'active' : ''}`}
              onClick={() => handlePresetChange(p.value)}
            >
              {p.label}
            </button>
          ))}
          {campaigns.length > 0 && (
            <select
              className="mkt-filter-select"
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              title="Filter visitor lists by ad campaign"
            >
              <option value="">All Campaigns</option>
              {campaigns.map((c) => (
                <option key={c} value={c}>📢 {c}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Custom date picker */}
      {selectedPreset === 'custom' && (
        <div className="mkt-funnel-custom-dates">
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="mkt-date-input"
          />
          <span>to</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="mkt-date-input"
          />
          <button
            className="mkt-btn mkt-btn-primary"
            onClick={fetchFunnel}
            disabled={!customStart || !customEnd}
          >
            Apply
          </button>
        </div>
      )}

      {/* Summary tiles — cumulative reached counts, % of landed, click for who */}
      <div className="mkt-funnel-summary">
        {TILES.map((tile) => (
          <div
            key={tile.key}
            className={`mkt-summary-card mkt-feedback-card ${selectedTile === tile.key ? 'active' : ''}`}
            onClick={() => setSelectedTile(selectedTile === tile.key ? null : tile.key)}
            title="Click to see who"
          >
            <span className="mkt-summary-label">{tile.label}</span>
            <span className="mkt-summary-value">{tileCount(tile).toLocaleString()}</span>
            {tilePct(tile) !== null && (
              <span className="mkt-summary-rate">{tilePct(tile)}% of landed</span>
            )}
            {tile.key === 'landed' && <span className="mkt-summary-rate">everyone who arrived</span>}
            {tile.key !== 'returning' && tile.key !== 'paid' && (
              <span className="mkt-summary-rate" style={{ color: '#dc2626' }}>
                {(stepById[tile.stepId]?.count ?? 0).toLocaleString()} stopped here
              </span>
            )}
          </div>
        ))}
        {summary.ga4Traffic?.sessions > 0 && (
          <div className="mkt-summary-card">
            <span className="mkt-summary-label">GA4 Sessions</span>
            <span className="mkt-summary-value">{summary.ga4Traffic.sessions.toLocaleString()}</span>
            <span className="mkt-summary-rate">{summary.ga4Traffic.users} users</span>
          </div>
        )}
      </div>

      {/* Tile drill-down: who's behind the number, incl. contact status */}
      {activeTile && (() => {
        const cohort = tileCohort(activeTile).filter(matchesCampaign)
          .sort((a, b) => b.stepIndex - a.stepIndex || new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
        return (
          <div className="mkt-funnel-visitors" style={{ marginBottom: '16px' }}>
            <div className="mkt-funnel-visitors-header">
              <span>
                {activeTile.key === 'returning' ? 'Returning visitors' : `Reached ${activeTile.label}`} ({cohort.length})
              </span>
              <button className="mkt-btn mkt-btn-secondary" style={{ fontSize: '12px', padding: '2px 10px' }} onClick={() => setSelectedTile(null)}>
                ✕ Close
              </button>
            </div>
            <div className="mkt-funnel-visitors-list">
              {cohort.slice(0, 100).map((p) => (
                <div key={p.id} className="mkt-funnel-visitor-row">
                  <div className="mkt-funnel-visitor-info">
                    <span className="mkt-visitor-name">{p.name || p.email || p.trademarkName || (p.tempUserId || p.browseId || '').slice(0, 12)}</span>
                    {p.email && p.name && <span className="mkt-visitor-date">{p.email}</span>}
                    {p.trademarkName && <span className="mkt-visitor-tm">{p.trademarkName}</span>}
                    {websiteChip(p, p.stepIndex >= 1)}
                    {p.registrationLikelihood && (
                      <span className="mkt-visitor-mode" style={likelihoodStyle(p.registrationLikelihood)} title="AI registration likelihood">
                        AI: {String(p.registrationLikelihood).replace(/_/g, ' ')}
                      </span>
                    )}
                    <span className="mkt-visitor-mode">{p.stepName}</span>
                    {p.isReturning && <span className="mkt-badge mkt-badge-returning">Returning</span>}
                    <span
                      className="mkt-visitor-date"
                      style={p.outreachCount > 0 ? { color: '#047857', fontWeight: 600 } : { color: '#b45309' }}
                    >
                      {contactLabel(p)}
                    </span>
                    {(p.adName || p.adGroup || p.adCampaign || p.utmCampaign) && (
                      <span className="mkt-visitor-mode" style={{ background: '#eff6ff', color: '#1d4ed8' }}>
                        📢 {p.adName || p.adGroup || p.adCampaign || p.utmCampaign}
                      </span>
                    )}
                    {(p.adKeyword || p.utmTerm) && (
                      <span
                        className="mkt-visitor-mode"
                        style={{ background: '#f0fdf4', color: '#15803d' }}
                        title={p.adMatchType ? `Search keyword (${String(p.adMatchType).toLowerCase()} match)` : 'Search keyword'}
                      >
                        🔍 {p.adKeyword || p.utmTerm}
                      </span>
                    )}
                  </div>
                  <div className="mkt-funnel-visitor-actions">
                    <button
                      className="mkt-btn-icon"
                      onClick={() => navigate(`/marketing/visitor/${p.id}`)}
                      title="View profile"
                    >
                      →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Keyword breakdown — how far each search keyword's visitors get */}
      {keywordRows.length > 0 && (
        <div className="mkt-funnel-visitors" style={{ marginBottom: '16px' }}>
          <div className="mkt-funnel-visitors-header">
            <span>🔍 Keywords ({keywordRows.length}{campaignFilter ? ` — ${campaignFilter}` : ''})</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ color: '#64748b' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Keyword</th>
                  {keywordSteps.map((s) => (
                    <th key={s.key} style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600 }}>{s.label}</th>
                  ))}
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600 }} title="Paid as % of landed">Paid %</th>
                </tr>
              </thead>
              <tbody>
                {keywordRows.map((r) => {
                  const paidPct = r.counts[0] ? Math.round((r.counts[paidCol] / r.counts[0]) * 100) : 0;
                  return (
                    <tr key={r.keyword} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ color: '#15803d', fontWeight: 600 }}>{r.keyword}</span>
                        {r.adGroup && (
                          <span style={{ color: '#94a3b8', marginLeft: '8px', fontSize: '12px' }}>{r.adGroup}</span>
                        )}
                      </td>
                      {r.counts.map((c, i) => (
                        <td
                          key={i}
                          style={{
                            textAlign: 'right',
                            padding: '8px 12px',
                            color: c ? (i === paidCol ? '#047857' : '#0f172a') : '#cbd5e1',
                            fontWeight: i === paidCol && c ? 700 : 400,
                          }}
                        >
                          {c || '–'}
                        </td>
                      ))}
                      <td style={{ textAlign: 'right', padding: '8px 12px', color: paidPct ? '#047857' : '#cbd5e1', fontWeight: paidPct ? 700 : 400 }}>
                        {paidPct ? `${paidPct}%` : '–'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Funnel visualization */}
      <div className="mkt-funnel-steps">
        {funnel.map((step, i) => {
          const isExpanded = expandedStep === step.id;
          const maxCount = funnel[0]?.reached || funnel[0]?.count || 1;
          const barWidth = Math.max(5, ((step.reached ?? step.count) / maxCount) * 100);

          return (
            <div key={step.id} className="mkt-funnel-step-row">
              {/* Step bar */}
              <div
                className={`mkt-funnel-step ${isExpanded ? 'expanded' : ''}`}
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
              >
                <div className="mkt-funnel-step-info">
                  <span className="mkt-step-index">{step.index + 1}</span>
                  <span className="mkt-step-name">
                    {step.name}
                    {step.id === 'checkout' && (
                      <span
                        style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 400, color: '#94a3b8', cursor: 'help' }}
                        title="Legacy pre-quote-first step. Its cumulative reached count includes quote-flow visitors who never saw Review & Pay; only the stopped-here drop-off is checkout-specific."
                      >
                        ⓘ count includes quote-flow
                      </span>
                    )}
                  </span>
                  <span className="mkt-step-count">
                    {(step.reached ?? step.count).toLocaleString()}
                    {step.ga4Events > 0 && (
                      <span className="mkt-step-ga4" title="GA4 events">
                        ({step.ga4Events.toLocaleString()} GA4)
                      </span>
                    )}
                  </span>
                </div>
                <div className="mkt-funnel-bar-container">
                  <div
                    className="mkt-funnel-bar"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <div className="mkt-funnel-step-meta">
                  <span className="mkt-step-rate">{step.conversionRate}%</span>
                  {i > 0 && step.dropOff > 0 && (
                    <span className="mkt-step-dropoff">
                      ↓ {step.dropOff} dropped ({step.dropOffRate}%)
                    </span>
                  )}
                </div>
              </div>

              {/* Expanded visitor list */}
              {isExpanded && step.visitors.filter(matchesCampaign).length > 0 && (
                <div className="mkt-funnel-visitors">
                  <div className="mkt-funnel-visitors-header">
                    <span>
                      Stopped at this step ({step.visitors.filter(matchesCampaign).length}
                      {campaignFilter ? ` of ${step.visitors.length} — ${campaignFilter}` : ''})
                    </span>
                  </div>
                  <div className="mkt-funnel-visitors-list">
                    {step.visitors.filter(matchesCampaign).slice(0, 50).map(v => (
                      <div key={v.id} className="mkt-funnel-visitor-row">
                        <div className="mkt-funnel-visitor-info">
                          <span className="mkt-visitor-name">
                            {v.name || v.email || v.trademarkName || v.browseId?.slice(0, 12) || v.tempUserId?.slice(0, 12)}
                          </span>
                          {v.isReturning && (
                            <span className="mkt-badge mkt-badge-returning" title="Returning visitor">
                              Returning
                            </span>
                          )}
                          {v.trademarkName && (
                            <span className="mkt-visitor-tm">{v.trademarkName}</span>
                          )}
                          {websiteChip(v, step.index >= 1)}
                          {v.registrationLikelihood && (
                            <span className="mkt-visitor-mode" style={likelihoodStyle(v.registrationLikelihood)} title="AI registration likelihood">
                              AI: {String(v.registrationLikelihood).replace(/_/g, ' ')}
                            </span>
                          )}
                          {v.trafficSource && v.trafficSource !== 'direct' && (
                            <span className="mkt-visitor-mode">{v.trafficSource}</span>
                          )}
                          {(v.adName || v.adGroup || v.adCampaign || v.utmCampaign) && (
                            <span
                              className="mkt-visitor-mode"
                              style={{ background: '#eff6ff', color: '#1d4ed8' }}
                              title={[v.adCampaign, v.adGroup].filter(Boolean).join(' · ') || 'Ad campaign'}
                            >
                              📢 {v.adName || v.adGroup || v.adCampaign || v.utmCampaign}
                            </span>
                          )}
                          {(v.adKeyword || v.utmTerm) && (
                            <span
                              className="mkt-visitor-mode"
                              style={{ background: '#f0fdf4', color: '#15803d' }}
                              title={v.adMatchType ? `Search keyword (${String(v.adMatchType).toLowerCase()} match)` : 'Search keyword'}
                            >
                              🔍 {v.adKeyword || v.utmTerm}
                            </span>
                          )}
                          {v.wizardMode && (
                            <span className="mkt-visitor-mode">{v.wizardMode}</span>
                          )}
                          {v.createdAt && (
                            <span className="mkt-visitor-date">
                              {new Date(v.createdAt).toLocaleDateString()}
                            </span>
                          )}
                          {v.cartValue && (
                            <span className="mkt-visitor-value">${v.cartValue}</span>
                          )}
                        </div>
                        <div className="mkt-funnel-visitor-actions">
                          {v.clarityUrl && (
                            <a
                              href={v.clarityUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mkt-btn-icon"
                              title="Watch Clarity session"
                            >
                              👁
                            </a>
                          )}
                          <button
                            className="mkt-btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/marketing/visitor/${v.id}`);
                            }}
                            title="View profile"
                          >
                            →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isExpanded && step.visitors.filter(matchesCampaign).length === 0 && (
                <div className="mkt-funnel-visitors">
                  <p className="mkt-funnel-empty">
                    {campaignFilter ? `No users from ${campaignFilter} at this step` : 'No users currently at this step'}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function timeAgoShort(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

export default Funnel;
